import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircleIcon, SearchIcon, BookOpenIcon, MessageCircleIcon, PhoneIcon, MailIcon, ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon, ArrowRightIcon, HeadphonesIcon, FileTextIcon, InfoIcon, ShieldIcon, CreditCardIcon, TicketIcon, UsersIcon, CalendarIcon } from 'lucide-react';
const HelpFAQs = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [expandedFaqs, setExpandedFaqs] = useState<string[]>([]);
  const toggleFaq = (faqId: string) => {
    if (expandedFaqs.includes(faqId)) {
      setExpandedFaqs(expandedFaqs.filter(id => id !== faqId));
    } else {
      setExpandedFaqs([...expandedFaqs, faqId]);
    }
  };
  const categories = [{
    id: 'general',
    name: 'General'
  }, {
    id: 'account',
    name: 'Account & Profile'
  }, {
    id: 'events',
    name: 'Events & Tickets'
  }, {
    id: 'communities',
    name: 'Communities'
  }, {
    id: 'jobs',
    name: 'Jobs'
  }, {
    id: 'payments',
    name: 'Payments & Billing'
  }];
  const faqs = {
    general: [{
      id: 'general-1',
      question: 'What is JoinToday.co.ke?',
      answer: 'JoinToday.co.ke is a community platform that connects Kenyans through events, communities, job opportunities, and more. Our mission is to help people connect, learn, and grow together.'
    }, {
      id: 'general-2',
      question: 'How do I get started on JoinToday?',
      answer: 'To get started, create an account by clicking the "Sign Up" button in the top right corner. Once registered, you can join communities, browse events, apply for jobs, and more.'
    }, {
      id: 'general-3',
      question: 'Is JoinToday free to use?',
      answer: 'Yes, basic access to JoinToday is free. This includes browsing communities, events, and job listings. Some premium features or event tickets may require payment.'
    }, {
      id: 'general-4',
      question: 'Which devices and browsers can I use to access JoinToday?',
      answer: 'JoinToday works on all modern browsers including Chrome, Firefox, Safari, and Edge. We also have a mobile-responsive website that works well on smartphones and tablets.'
    }],
    account: [{
      id: 'account-1',
      question: 'How do I create an account?',
      answer: 'Click the "Sign Up" button in the top navigation bar. You can register using your email, or sign up with Google or Facebook for faster access.'
    }, {
      id: 'account-2',
      question: 'How do I reset my password?',
      answer: 'Click on "Sign In", then select "Forgot Password". Enter your email address and follow the instructions sent to your email to reset your password.'
    }, {
      id: 'account-3',
      question: 'Can I change my username or email address?',
      answer: 'Yes, you can update your username, email, and other profile information by going to your Profile page and clicking on "Edit Profile".'
    }, {
      id: 'account-4',
      question: 'How do I delete my account?',
      answer: 'To delete your account, go to your Profile settings, scroll to the bottom, and click on "Delete Account". Please note that this action is permanent and cannot be undone.'
    }],
    events: [{
      id: 'events-1',
      question: 'How do I purchase tickets for an event?',
      answer: 'Browse events on the Events page, select the event you\'re interested in, and click "Get Tickets". Follow the checkout process to complete your purchase.'
    }, {
      id: 'events-2',
      question: 'Can I get a refund for my ticket?',
      answer: 'Refund policies vary by event. Check the event details page for specific refund information. Generally, refunds may be available up to 48 hours before the event starts.'
    }, {
      id: 'events-3',
      question: 'How do I transfer my ticket to someone else?',
      answer: 'Go to "My Tickets" in your profile, find the ticket you want to transfer, and click on "Gift". Enter the recipient\'s email address and they\'ll receive instructions to claim the ticket.'
    }, {
      id: 'events-4',
      question: 'How do I create my own event?',
      answer: 'You need a Business account to create events. If you have one, go to the Events page and click "Create Event". If you have a Personal account, you\'ll need to upgrade to a Business account first.'
    }],
    communities: [{
      id: 'communities-1',
      question: 'How do I join a community?',
      answer: 'Browse communities on the Communities page, select one you\'re interested in, and click "Join Community". Some communities may require approval from administrators.'
    }, {
      id: 'communities-2',
      question: 'Can I create my own community?',
      answer: 'Yes, you can create a community by going to the Communities page and clicking "Create Community". Follow the steps to set up your community name, description, and rules.'
    }, {
      id: 'communities-3',
      question: 'How do I invite others to my community?',
      answer: 'As a community admin, go to your community page, click "Manage", then "Invite Members". You can invite people via email or generate a shareable link.'
    }, {
      id: 'communities-4',
      question: 'How do I report inappropriate content in a community?',
      answer: 'Click the three dots (...) next to any post or comment, then select "Report". Choose the reason for reporting and submit. Our moderation team will review it promptly.'
    }],
    jobs: [{
      id: 'jobs-1',
      question: 'How do I apply for a job?',
      answer: "Browse jobs on the Jobs page, select a job you're interested in, and click \"Apply Now\". You'll either apply directly on JoinToday or be redirected to the employer's application site."
    }, {
      id: 'jobs-2',
      question: 'How do I post a job listing?',
      answer: 'You need a Business account to post jobs. If you have one, go to the Jobs page and click "Post a Job". If you have a Personal account, you\'ll need to upgrade to a Business account first.'
    }, {
      id: 'jobs-3',
      question: 'How much does it cost to post a job?',
      answer: 'Basic job postings start at KES 2,000 for 30 days. Premium listings with higher visibility and featured status start at KES 5,000. See our pricing page for more details.'
    }, {
      id: 'jobs-4',
      question: 'How do I save jobs to apply later?',
      answer: 'Click the bookmark icon on any job listing to save it. You can view all saved jobs in your profile under "Saved Jobs".'
    }],
    payments: [{
      id: 'payments-1',
      question: 'What payment methods do you accept?',
      answer: 'We accept M-Pesa, credit/debit cards (Visa, Mastercard), and bank transfers for payments on JoinToday.'
    }, {
      id: 'payments-2',
      question: 'Is my payment information secure?',
      answer: 'Yes, we use industry-standard encryption and security measures to protect your payment information. We do not store your full credit card details on our servers.'
    }, {
      id: 'payments-3',
      question: 'How do I get an invoice for my purchase?',
      answer: 'After completing a purchase, an invoice is automatically generated and sent to your email. You can also find all your invoices in your profile under "Payment History".'
    }, {
      id: 'payments-4',
      question: 'What should I do if my payment fails?',
      answer: 'If your payment fails, first check that your payment details are correct. If the issue persists, try a different payment method or contact your bank. If you still have problems, contact our support team.'
    }]
  };
  return <div className="pb-16" data-id="element-2109">
      <div className="border-b border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800" data-id="element-2110">
        <div className="max-w-4xl mx-auto p-4" data-id="element-2111">
          <h1 className="text-2xl font-bold mb-2 flex items-center dark:text-gray-200" data-id="element-2112">
            <HelpCircleIcon className="w-6 h-6 mr-2" data-id="element-2113" />
            Help Center & FAQs
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-2114">
            Find answers to common questions and get support
          </p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-4" data-id="element-2115">
        {/* Search */}
        <div className="mb-6" data-id="element-2116">
          <div className="relative" data-id="element-2117">
            <input type="text" placeholder="Search for help articles or FAQs..." className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-gray-200" data-id="element-2118" />
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-2119" />
          </div>
        </div>
        {/* Quick Help Sections */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-2120">
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center" data-id="element-2121">
            <div className="w-12 h-12 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-2" data-id="element-2122">
              <BookOpenIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" data-id="element-2123" />
            </div>
            <h3 className="font-medium mb-1 dark:text-gray-200" data-id="element-2124">Help Guides</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-2125">
              Detailed guides on how to use JoinToday features
            </p>
            <Link to="/help&FAQs/guides" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2126">
              Browse Guides
            </Link>
          </div>
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center" data-id="element-2127">
            <div className="w-12 h-12 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-2" data-id="element-2128">
              <MessageCircleIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" data-id="element-2129" />
            </div>
            <h3 className="font-medium mb-1 dark:text-gray-200" data-id="element-2130">
              Community Support
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-2131">
              Ask questions and get help from other users
            </p>
            <Link to="/forum?category=support" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2132">
              Visit Support Forum
            </Link>
          </div>
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center" data-id="element-2133">
            <div className="w-12 h-12 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-2" data-id="element-2134">
              <HeadphonesIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" data-id="element-2135" />
            </div>
            <h3 className="font-medium mb-1 dark:text-gray-200" data-id="element-2136">
              Contact Support
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-2137">
              Get in touch with our customer support team
            </p>
            <Link to="/contact" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2138">
              Contact Us
            </Link>
          </div>
        </div>
        {/* FAQs */}
        <div className="mb-8" data-id="element-2139">
          <h2 className="text-xl font-bold mb-4 dark:text-gray-200" data-id="element-2140">
            Frequently Asked Questions
          </h2>
          {/* FAQ Categories */}
          <div className="flex overflow-x-auto space-x-2 pb-2 mb-4" data-id="element-2141">
            {categories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`px-3 py-1 border rounded-full whitespace-nowrap text-sm ${activeCategory === category.id ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800' : 'border-gray-300 dark:border-gray-600 dark:text-gray-200'}`} data-id="element-2142">
                {category.name}
              </button>)}
          </div>
          {/* FAQ Accordion */}
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden" data-id="element-2143">
            {faqs[activeCategory as keyof typeof faqs].map((faq, index) => <div key={faq.id} className={`border-b border-gray-300 dark:border-gray-600 ${index === faqs[activeCategory as keyof typeof faqs].length - 1 ? 'border-b-0' : ''}`} data-id="element-2144">
                <button onClick={() => toggleFaq(faq.id)} className="w-full text-left p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2145">
                  <span className="font-medium dark:text-gray-200" data-id="element-2146">
                    {faq.question}
                  </span>
                  {expandedFaqs.includes(faq.id) ? <ChevronUpIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-2147" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-2148" />}
                </button>
                {expandedFaqs.includes(faq.id) && <div className="p-4 pt-0 text-gray-600 dark:text-gray-400 text-sm" data-id="element-2149">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </div>
        {/* Help Topics */}
        <div className="mb-8" data-id="element-2150">
          <h2 className="text-xl font-bold mb-4 dark:text-gray-200" data-id="element-2151">
            Help Topics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2152">
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-2153">
              <div className="flex items-center mb-3" data-id="element-2154">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-3" data-id="element-2155">
                  <UsersIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" data-id="element-2156" />
                </div>
                <h3 className="font-medium dark:text-gray-200" data-id="element-2157">Communities</h3>
              </div>
              <ul className="space-y-2 pl-12" data-id="element-2158">
                <li data-id="element-2159">
                  <Link to="/help&FAQs/communities/join" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2160">
                    How to join communities
                  </Link>
                </li>
                <li data-id="element-2161">
                  <Link to="/help&FAQs/communities/create" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2162">
                    Creating your own community
                  </Link>
                </li>
                <li data-id="element-2163">
                  <Link to="/help&FAQs/communities/moderate" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2164">
                    Moderating your community
                  </Link>
                </li>
              </ul>
            </div>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-2165">
              <div className="flex items-center mb-3" data-id="element-2166">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-3" data-id="element-2167">
                  <CalendarIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" data-id="element-2168" />
                </div>
                <h3 className="font-medium dark:text-gray-200" data-id="element-2169">Events</h3>
              </div>
              <ul className="space-y-2 pl-12" data-id="element-2170">
                <li data-id="element-2171">
                  <Link to="/help&FAQs/events/find" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2172">
                    Finding and attending events
                  </Link>
                </li>
                <li data-id="element-2173">
                  <Link to="/help&FAQs/events/create" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2174">
                    Creating and managing events
                  </Link>
                </li>
                <li data-id="element-2175">
                  <Link to="/help&FAQs/events/tickets" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2176">
                    Ticket types and management
                  </Link>
                </li>
              </ul>
            </div>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-2177">
              <div className="flex items-center mb-3" data-id="element-2178">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-3" data-id="element-2179">
                  <TicketIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" data-id="element-2180" />
                </div>
                <h3 className="font-medium dark:text-gray-200" data-id="element-2181">Tickets</h3>
              </div>
              <ul className="space-y-2 pl-12" data-id="element-2182">
                <li data-id="element-2183">
                  <Link to="/help&FAQs/tickets/purchase" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2184">
                    Purchasing tickets
                  </Link>
                </li>
                <li data-id="element-2185">
                  <Link to="/help&FAQs/tickets/refunds" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2186">
                    Refunds and exchanges
                  </Link>
                </li>
                <li data-id="element-2187">
                  <Link to="/help&FAQs/tickets/transfer" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2188">
                    Transferring tickets
                  </Link>
                </li>
              </ul>
            </div>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-2189">
              <div className="flex items-center mb-3" data-id="element-2190">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-3" data-id="element-2191">
                  <CreditCardIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" data-id="element-2192" />
                </div>
                <h3 className="font-medium dark:text-gray-200" data-id="element-2193">Payments</h3>
              </div>
              <ul className="space-y-2 pl-12" data-id="element-2194">
                <li data-id="element-2195">
                  <Link to="/help&FAQs/payments/methods" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2196">
                    Payment methods
                  </Link>
                </li>
                <li data-id="element-2197">
                  <Link to="/help&FAQs/payments/security" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2198">
                    Payment security
                  </Link>
                </li>
                <li data-id="element-2199">
                  <Link to="/help&FAQs/payments/invoices" className="text-sm text-gray-800 dark:text-gray-200 hover:underline" data-id="element-2200">
                    Invoices and receipts
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
        {/* Contact Support */}
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 mb-8" data-id="element-2201">
          <h2 className="text-xl font-bold mb-4 dark:text-gray-200" data-id="element-2202">
            Still Need Help?
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4" data-id="element-2203">
            Our support team is available to assist you. Get in touch with us
            through any of these channels:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2204">
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center" data-id="element-2205">
              <div className="w-12 h-12 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-2" data-id="element-2206">
                <PhoneIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" data-id="element-2207" />
              </div>
              <h3 className="font-medium mb-1 dark:text-gray-200" data-id="element-2208">
                Phone Support
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-2209">
                +254 (0) 100 254 254
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2210">
                Mon-Fri, 8am-6pm EAT
              </p>
            </div>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center" data-id="element-2211">
              <div className="w-12 h-12 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-2" data-id="element-2212">
                <MailIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" data-id="element-2213" />
              </div>
              <h3 className="font-medium mb-1 dark:text-gray-200" data-id="element-2214">
                Email Support
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-2215">
                help@jointoday.co.ke
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2216">
                24/7 support, response within 24 hours
              </p>
            </div>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center" data-id="element-2217">
              <div className="w-12 h-12 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-2" data-id="element-2218">
                <MessageCircleIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" data-id="element-2219" />
              </div>
              <h3 className="font-medium mb-1 dark:text-gray-200" data-id="element-2220">Live Chat</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-2221">
                Chat with our support team
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2222">
                Available 9am-5pm EAT, weekdays
              </p>
            </div>
          </div>
          <div className="mt-6 text-center" data-id="element-2223">
            <Link to="/contact" className="inline-flex items-center px-4 py-2 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md" data-id="element-2224">
              Contact Support
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2225" />
            </Link>
          </div>
        </div>
        {/* Additional Resources */}
        <div data-id="element-2226">
          <h2 className="text-xl font-bold mb-4 dark:text-gray-200" data-id="element-2227">
            Additional Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2228">
            <Link to="/terms" className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2229">
              <div className="flex items-center" data-id="element-2230">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-3" data-id="element-2231">
                  <FileTextIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" data-id="element-2232" />
                </div>
                <div data-id="element-2233">
                  <h3 className="font-medium dark:text-gray-200" data-id="element-2234">
                    Terms of Service
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2235">
                    Review our terms and conditions
                  </p>
                </div>
              </div>
            </Link>
            <Link to="/privacy" className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2236">
              <div className="flex items-center" data-id="element-2237">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-3" data-id="element-2238">
                  <ShieldIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" data-id="element-2239" />
                </div>
                <div data-id="element-2240">
                  <h3 className="font-medium dark:text-gray-200" data-id="element-2241">
                    Privacy Policy
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2242">
                    Learn how we protect your data
                  </p>
                </div>
              </div>
            </Link>
            <Link to="/about" className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2243">
              <div className="flex items-center" data-id="element-2244">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-3" data-id="element-2245">
                  <InfoIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" data-id="element-2246" />
                </div>
                <div data-id="element-2247">
                  <h3 className="font-medium dark:text-gray-200" data-id="element-2248">
                    About JoinToday
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2249">
                    Learn more about our platform
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>;
};
export default HelpFAQs;